/**
 * Withings Token Service
 * Handles OAuth2 authentication and token management for Withings API
 */

import type { WithingsAuthConfig, WithingsTokenData } from "../types/domain";

import { core } from "@tauri-apps/api";
import { fetch } from "@tauri-apps/plugin-http";

import { debugHealth } from "@/domains/logging/utils/debugLog";

interface WithingsOAuthBody {
  access_token?: string;
  expires_in?: number;
  refresh_token?: string;
  scope?: string;
  token_type?: string;
  userid?: string;
}

interface WithingsOAuthResponse {
  body?: undefined | WithingsOAuthBody;
  error?: string;
  status?: number;
}

/**
 * Withings Token Service
 * Manages OAuth2 authentication flow and token storage
 */
export const WithingsTokenService = {
  // Rate limiting protection
  _lastApiCall: 0,
  get lastApiCall(): number {
    return this._lastApiCall;
  },
  set lastApiCall(value: number) {
    // Reason: Direct assignment to private property for rate limiting
    (WithingsTokenService as { _lastApiCall: number })._lastApiCall = value;
  },
  MIN_API_INTERVAL: 10_000 as const, // 10 seconds minimum between API calls

  /**
   * Check rate limiting and wait if necessary
   *
   * @returns Promise resolving when rate limit check is complete
   *
   */
  async checkRateLimit(): Promise<void> {
    const now = Date.now();
    const timeSinceLastCall = now - WithingsTokenService.lastApiCall;

    if (timeSinceLastCall < WithingsTokenService.MIN_API_INTERVAL) {
      const waitTime = WithingsTokenService.MIN_API_INTERVAL - timeSinceLastCall;
      await new Promise((resolve) => setTimeout(resolve, waitTime));
    }

    WithingsTokenService.lastApiCall = Date.now();
  },

  /**
   * Handle Withings API error responses
   *
   * @param {WithingsOAuthResponse} responseData - API response data
   *
   * @returns {void} void
   *
   */
  handleApiError(responseData: WithingsOAuthResponse): void {
    if (responseData.status === 601) {
      debugHealth("error", "withings", "Rate limit exceeded (601)", {
        file: "withings-token-service.ts",
        function: "handleApiError",
      });
      throw new Error("Rate limit exceeded. Please wait at least 10 seconds before trying again.");
    }

    if (responseData.status === 401) {
      debugHealth("error", "withings", "Invalid authorization code (401)", {
        file: "withings-token-service.ts",
        function: "handleApiError",
      });
      throw new Error("Invalid authorization code. Please try authenticating again.");
    }

    if (responseData.status === 400) {
      debugHealth("error", "withings", "Invalid request parameters (400)", {
        file: "withings-token-service.ts",
        function: "handleApiError",
      });
      throw new Error("Invalid request parameters. Please check your configuration.");
    }

    // Generic error with status code
    const errorMessage = responseData.error ?? "Unknown error";
    const statusCode = responseData.status ?? 0;
    debugHealth("error", "withings", "Generic Withings API error", {
      error: errorMessage,
      file: "withings-token-service.ts",
      function: "handleApiError",
      status: statusCode,
    });
    throw new Error(`Withings API error (${String(statusCode)}): ${errorMessage}`);
  },

  /**
   * Initialize Withings authentication configuration
   *
   * @param {WithingsAuthConfig} config - Authentication configuration
   *
   * @returns {Promise<void>} Promise resolving when configuration is initialized
   *
   */
  async initializeConfig(config: WithingsAuthConfig): Promise<void> {
    try {
      debugHealth("info", "withings", "Initializing Withings configuration", {
        clientId: config.clientId,
        file: "withings-token-service.ts",
        function: "initializeConfig",
        redirectUri: config.redirectUri,
      });

      await core.invoke("set_withings_config", { config });

      debugHealth("info", "withings", "Withings configuration initialized successfully", {
        file: "withings-token-service.ts",
        function: "initializeConfig",
      });
    } catch (error: unknown) {
      debugHealth("error", "withings", "Failed to initialize Withings configuration", {
        error: error instanceof Error ? error.message : String(error),
        file: "withings-token-service.ts",
        function: "initializeConfig",
      });
      throw new Error("Failed to initialize Withings configuration");
    }
  },

  /**
   * Get stored authentication configuration
   *
   * @returns {Promise<undefined | WithingsAuthConfig>} Promise resolving to configuration or undefined
   *
   */
  async getConfig(): Promise<undefined | WithingsAuthConfig> {
    try {
      const { loadWithingsConfig } = await import("./withingsConfigService");
      const config = await loadWithingsConfig();

      return config ?? undefined;
    } catch {
      return undefined;
    }
  },

  /**
   * Generate OAuth2 authorization URL
   *
   * @returns {Promise<string>} Promise resolving to authorization URL
   *
   */
  async generateAuthUrl(): Promise<string> {
    debugHealth("info", "withings", "Generating OAuth2 authorization URL", {
      file: "withings-token-service.ts",
      function: "generateAuthUrl",
    });

    const config = await WithingsTokenService.getConfig();
    if (!config) {
      debugHealth("error", "withings", "Withings configuration not found", {
        file: "withings-token-service.ts",
        function: "generateAuthUrl",
      });
      throw new Error(
        "Withings configuration not found. Please configure your API credentials in Settings → Withings first.",
      );
    }

    debugHealth("debug", "withings", "Retrieved Withings configuration", {
      file: "withings-token-service.ts",
      function: "generateAuthUrl",
      hasClientId: !!config.clientId,
      hasClientSecret: !!config.clientSecret,
      redirectUri: config.redirectUri,
    });

    if (!config.clientId || config.clientId.trim() === "") {
      debugHealth("error", "withings", "Withings Client ID is missing", {
        file: "withings-token-service.ts",
        function: "generateAuthUrl",
      });
      throw new Error(
        "Withings Client ID is required. Please configure it in Settings → Withings.",
      );
    }

    if (!config.clientSecret || config.clientSecret.trim() === "") {
      debugHealth("error", "withings", "Withings Client Secret is missing", {
        file: "withings-token-service.ts",
        function: "generateAuthUrl",
      });
      throw new Error(
        "Withings Client Secret is required. Please configure it in Settings → Withings.",
      );
    }

    // Generate state parameter for CSRF protection
    const state = WithingsTokenService.generateState();
    debugHealth("debug", "withings", "Generated OAuth state parameter", {
      file: "withings-token-service.ts",
      function: "generateAuthUrl",
      state: state,
    });

    // Store state for validation later
    await WithingsTokenService.storeState(state);
    debugHealth("info", "withings", "OAuth state parameter stored", {
      file: "withings-token-service.ts",
      function: "generateAuthUrl",
    });

    const parameters = new URLSearchParams({
      client_id: config.clientId,
      redirect_uri: config.redirectUri,
      response_type: "code",
      scope: "user.info,user.metrics,user.activity",
      state: state,
    });

    const authUrl = `${config.authUrl}?${parameters.toString()}`;

    debugHealth("info", "withings", "Generated OAuth2 authorization URL", {
      authUrl: authUrl,
      file: "withings-token-service.ts",
      function: "generateAuthUrl",
      state: state,
    });

    return authUrl;
  },

  /**
   * Exchange authorization code for access token
   *
   * @param {string} code - Authorization code from OAuth callback
   *
   * @param {string} [state] - Optional state parameter for CSRF protection
   *
   * @returns {Promise<WithingsTokenData>} Promise resolving to token data
   *
   */
  async exchangeCodeForToken(code: string, state?: string): Promise<WithingsTokenData> {
    debugHealth("info", "withings", "Exchanging authorization code for access token", {
      code: code,
      file: "withings-token-service.ts",
      function: "exchangeCodeForToken",
      state: state,
    });

    const config = await WithingsTokenService.getConfig();
    if (!config) {
      debugHealth("error", "withings", "Withings configuration not found during token exchange", {
        file: "withings-token-service.ts",
        function: "exchangeCodeForToken",
      });
      throw new Error("Withings configuration not found");
    }

    debugHealth("debug", "withings", "Retrieved configuration for token exchange", {
      file: "withings-token-service.ts",
      function: "exchangeCodeForToken",
      hasClientId: !!config.clientId,
      hasClientSecret: !!config.clientSecret,
      redirectUri: config.redirectUri,
    });

    // Validate state parameter if provided
    if (state) {
      debugHealth("debug", "withings", "Validating state parameter", {
        file: "withings-token-service.ts",
        function: "exchangeCodeForToken",
        receivedState: state,
      });

      const storedState = await WithingsTokenService.getStoredState();
      debugHealth("debug", "withings", "Retrieved stored state parameter", {
        file: "withings-token-service.ts",
        function: "exchangeCodeForToken",
        storedState: storedState,
      });

      if (!storedState || storedState !== state) {
        debugHealth("error", "withings", "State parameter mismatch - possible CSRF attack", {
          file: "withings-token-service.ts",
          function: "exchangeCodeForToken",
          receivedState: state,
          storedState: storedState,
        });
        throw new Error("Invalid state parameter - possible CSRF attack");
      }

      debugHealth("info", "withings", "State parameter validation successful", {
        file: "withings-token-service.ts",
        function: "exchangeCodeForToken",
      });

      // Clear the used state
      await WithingsTokenService.clearStoredState();
      debugHealth("info", "withings", "Cleared used state parameter", {
        file: "withings-token-service.ts",
        function: "exchangeCodeForToken",
      });
    }

    // Apply rate limiting protection
    await WithingsTokenService.checkRateLimit();
    debugHealth("debug", "withings", "Rate limiting check passed", {
      file: "withings-token-service.ts",
      function: "exchangeCodeForToken",
    });

    try {
      debugHealth("info", "withings", "Making request to Withings OAuth2 endpoint", {
        clientId: config.clientId,
        endpoint: "https://wbsapi.withings.net/v2/oauth2",
        file: "withings-token-service.ts",
        function: "exchangeCodeForToken",
        redirectUri: config.redirectUri,
      });

      // Use the official Withings OAuth2 endpoint
      const response = await fetch("https://wbsapi.withings.net/v2/oauth2", {
        body: new URLSearchParams({
          action: "requesttoken",
          client_id: config.clientId,
          client_secret: config.clientSecret,
          code: code,
          grant_type: "authorization_code",
          redirect_uri: config.redirectUri,
        }).toString(),
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
        },
        method: "POST",
      });

      debugHealth("debug", "withings", "Received response from Withings API", {
        file: "withings-token-service.ts",
        function: "exchangeCodeForToken",
        ok: response.ok,
        status: response.status,
        statusText: response.statusText,
      });

      if (!response.ok) {
        const errorText = await response.text();
        debugHealth("error", "withings", "Withings API returned error response", {
          errorText: errorText,
          file: "withings-token-service.ts",
          function: "exchangeCodeForToken",
          status: response.status,
          statusText: response.statusText,
        });
        throw new Error(`Withings API error: ${String(response.status)} ${response.statusText}`);
      }

      const responseData = (await response.json()) as WithingsOAuthResponse;
      debugHealth("debug", "withings", "Parsed response data from Withings API", {
        error: responseData.error,
        file: "withings-token-service.ts",
        function: "exchangeCodeForToken",
        hasAccessToken: !!responseData.body?.access_token,
        hasBody: !!responseData.body,
        status: responseData.status,
      });

      // Check if the response has the expected structure
      if (!responseData.body?.access_token) {
        debugHealth("error", "withings", "Withings API response missing access token", {
          file: "withings-token-service.ts",
          function: "exchangeCodeForToken",
          responseData: responseData,
        });

        WithingsTokenService.handleApiError(responseData);
      }

      const body = responseData.body;
      const tokenData: WithingsTokenData = {
        accessToken: body?.access_token ?? "",
        expiresAt: Date.now() + (body?.expires_in ?? 10_800) * 1000,
        expiresIn: body?.expires_in ?? 10_800, // 3 hours default
        refreshExpiresAt: Date.now() + 365 * 24 * 60 * 60 * 1000, // 365 Tage für Refresh Token
        refreshToken: body?.refresh_token ?? "",
        scope: body?.scope ?? "",
        tokenType: body?.token_type ?? "Bearer",
        userId: body?.userid ?? "",
      };

      debugHealth("info", "withings", "Successfully parsed token data from Withings API", {
        expiresIn: tokenData.expiresIn,
        file: "withings-token-service.ts",
        function: "exchangeCodeForToken",
        hasAccessToken: !!tokenData.accessToken,
        hasRefreshToken: !!tokenData.refreshToken,
        userId: tokenData.userId,
      });

      await WithingsTokenService.storeTokens(tokenData);
      debugHealth("info", "withings", "Token exchange completed successfully", {
        file: "withings-token-service.ts",
        function: "exchangeCodeForToken",
      });

      return tokenData;
    } catch (error: unknown) {
      debugHealth("error", "withings", "Failed to exchange authorization code for token", {
        error: error instanceof Error ? error.message : String(error),
        file: "withings-token-service.ts",
        function: "exchangeCodeForToken",
      });
      throw new Error("Failed to authenticate with Withings");
    }
  },

  /**
   * Refresh access token using refresh token
   *
   * @returns {Promise<WithingsTokenData>} Promise resolving to refreshed token data
   *
   */
  async refreshToken(): Promise<WithingsTokenData> {
    const config = await WithingsTokenService.getConfig();
    const currentTokens = await WithingsTokenService.getStoredTokens();

    if (!config || !currentTokens) {
      throw new Error("Configuration or tokens not found");
    }

    // Apply rate limiting protection (wichtig gegen 601-Fehler)
    await WithingsTokenService.checkRateLimit();
    try {
      const response = await fetch(config.tokenUrl, {
        body: new URLSearchParams({
          action: "requesttoken",
          client_id: config.clientId,
          client_secret: config.clientSecret,
          grant_type: "refresh_token",
          refresh_token: currentTokens.refreshToken,
        }).toString(),
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
        },
        method: "POST",
      });

      if (!response.ok) {
        await response.text(); // NOTE: Consume response body
        throw new Error(
          `Withings refresh error: ${String(response.status)} ${response.statusText}`,
        );
      }

      const raw = (await response.json()) as WithingsOAuthBody | WithingsOAuthResponse;

      let body: WithingsOAuthBody;
      let status: number | undefined;
      let errorText: string | undefined;

      if ("body" in raw) {
        body = raw.body ?? {};
        status = raw.status;
        errorText = raw.error;
      } else {
        body = raw as WithingsOAuthBody;
      }

      // Check for Withings API errors (status != 0)
      if (typeof status === "number" && status !== 0) {
        throw new Error(`Withings API error (${String(status)}): ${errorText ?? "unknown"}`);
      }

      if (!body.access_token) {
        throw new Error("Withings refresh response missing access token");
      }

      // Prüfe ob sich der Refresh Token geändert hat
      const newRefreshToken = body.refresh_token ?? currentTokens.refreshToken;
      const refreshTokenChanged = newRefreshToken !== currentTokens.refreshToken;

      // Setze refreshExpiresAt: 365 Tage wenn neuer Token, sonst behalte altes Datum
      const refreshExpiresAt = refreshTokenChanged
        ? Date.now() + 365 * 24 * 60 * 60 * 1000 // Neuer Token: 365 Tage
        : currentTokens.refreshExpiresAt; // Gleicher Token: behalte altes Datum

      const tokenData: WithingsTokenData = {
        accessToken: body.access_token,
        expiresAt: Date.now() + (body.expires_in ?? 10_800) * 1000,
        expiresIn: body.expires_in ?? 10_800,
        refreshExpiresAt: refreshExpiresAt,
        refreshToken: newRefreshToken,
        scope: body.scope ?? "",
        tokenType: body.token_type ?? "Bearer",
        userId: body.userid ?? "",
      };

      await WithingsTokenService.storeTokens(tokenData);
      return tokenData;
    } catch (error: unknown) {
      // Only clear tokens for specific error conditions that indicate invalid tokens
      const errorMessage = error instanceof Error ? error.message : String(error);

      // Clear tokens only for clear authentication failures
      if (
        errorMessage.includes("invalid_grant") ||
        errorMessage.includes("401") ||
        errorMessage.includes("unauthorized") ||
        errorMessage.includes("expired")
      ) {
        await WithingsTokenService.clearTokens();
      } else {
        // Don't clear tokens for network errors, temporary API issues, etc.
      }

      throw new Error("Failed to refresh Withings token");
    }
  },

  /**
   * Get valid access token (refresh if necessary)
   *
   * @returns {Promise<string>} Promise resolving to valid access token
   *
   */
  async getValidAccessToken(): Promise<string> {
    const tokens = await WithingsTokenService.getStoredTokens();
    if (!tokens) {
      throw new Error("No Withings tokens found. Please authenticate first.");
    }

    // Check if token is expired (with 5 minute buffer)
    const now = Date.now();
    const buffer = 5 * 60 * 1000; // 5 minutes

    if (tokens.expiresAt - now < buffer) {
      const refreshedTokens = await WithingsTokenService.refreshToken();
      return refreshedTokens.accessToken;
    }

    return tokens.accessToken;
  },

  /**
   * Store tokens in secure storage using new OAuth2 TokenManager
   *
   * @param {WithingsTokenData} tokens - Token data to store
   *
   * @returns {Promise<void>} Promise resolving when tokens are stored
   *
   */
  async storeTokens(tokens: WithingsTokenData): Promise<void> {
    try {
      debugHealth("info", "withings", "Storing authentication tokens", {
        expiresIn: tokens.expiresIn,
        file: "withings-token-service.ts",
        function: "storeTokens",
        hasAccessToken: !!tokens.accessToken,
        hasRefreshToken: !!tokens.refreshToken,
        userId: tokens.userId,
      });

      const requestData = {
        request: {
          access_token: tokens.accessToken,
          // neu: serverseitig auch ein festes expires_at (Sekunden seit Epoch)
          expires_at: Math.floor(tokens.expiresAt / 1000),
          expires_in: tokens.expiresIn,
          provider: "withings",
          // neu: refresh_expires_at für korrekte 365-Tage-Anzeige
          refresh_expires_at: Math.floor(tokens.refreshExpiresAt / 1000),
          refresh_expires_in: undefined,
          refresh_token: tokens.refreshToken,
        },
      };

      debugHealth("debug", "withings", "Calling store_tokens command", {
        file: "withings-token-service.ts",
        function: "storeTokens",
        requestData: requestData,
      });

      await core.invoke("store_tokens", requestData);
      await core.invoke("store_tokens", requestData);

      debugHealth("info", "withings", "Authentication tokens stored successfully", {
        file: "withings-token-service.ts",
        function: "storeTokens",
      });
    } catch (error: unknown) {
      debugHealth("error", "withings", "Failed to store authentication tokens", {
        error: error instanceof Error ? error.message : String(error),
        file: "withings-token-service.ts",
        function: "storeTokens",
      });
      throw new Error("Failed to store authentication tokens");
    }
  },

  /**
   * Debug method to test parameter structure
   *
   * @returns {Promise<void>} Promise resolving when debug is complete
   *
   */
  async debugParameterStructure(): Promise<void> {
    try {
      // Test with different parameter naming variants
      const testParameters1 = { provider: "withings" };
      const testParameters2 = { providerName: "withings" };

      await core.invoke("debug_parameter_structure", testParameters1);
      await core.invoke("debug_parameter_structure", testParameters2);
      // NOTE: Results are for debugging only, not used
    } catch (error: unknown) {
      // NOTE: Debug function failed - log via debug system
      debugHealth("error", "withings", "Debug parameter structure error", {
        error: error instanceof Error ? error.message : String(error),
        file: "withings-token-service.ts",
        function: "debugParameterStructure",
      });
    }
  },

  /**
   * Get stored tokens using new OAuth2 TokenManager
   *
   * @returns {Promise<undefined | WithingsTokenData>} Promise resolving to token data or undefined
   *
   */
  async getStoredTokens(): Promise<undefined | WithingsTokenData> {
    try {
      // Call Tauri command with proper parameter structure
      const parameters = { provider: "withings" };

      const response = await core.invoke("get_stored_tokens", parameters);

      // Check if response has the expected structure
      interface TokenManagerData {
        access_token: string;
        expires_at?: number;
        expires_in?: number;
        refresh_expires_at?: number;
        refresh_token: string;
        scope?: string;
        token_type?: string;
        userid?: string;
      }

      interface TokenResponse {
        data?: TokenManagerData | undefined;
      }

      const tokenResponse = response as TokenResponse;
      if (!tokenResponse.data) {
        return undefined;
      }

      const tokens = tokenResponse.data;

      // Convert from OAuth2 TokenManager format back to WithingsTokenData

      return {
        accessToken: tokens.access_token,
        expiresAt: tokens.expires_at
          ? tokens.expires_at * 1000
          : Date.now() + (tokens.expires_in ?? 0) * 1000,
        expiresIn: tokens.expires_in ?? 0,
        refreshExpiresAt: tokens.refresh_expires_at
          ? tokens.refresh_expires_at * 1000
          : Date.now() + 365 * 24 * 60 * 60 * 1000, // Fallback: 365 Tage
        refreshToken: tokens.refresh_token,
        scope: tokens.scope ?? "",
        tokenType: tokens.token_type ?? "Bearer",
        userId: tokens.userid ?? "",
      };
    } catch {
      return undefined;
    }
  },

  /**
   * Clear stored tokens using new OAuth2 TokenManager
   *
   * @returns {Promise<void>} Promise resolving when tokens are cleared
   *
   */
  async clearTokens(): Promise<void> {
    try {
      const parameters = { provider: "withings" };

      await core.invoke("clear_tokens", { params: parameters });
    } catch (error: unknown) {
      // NOTE: Error clearing tokens - log via debug system
      debugHealth("error", "withings", "Error clearing tokens", {
        error: error instanceof Error ? error.message : String(error),
        file: "withings-token-service.ts",
        function: "clearTokens",
      });
    }
  },

  /**
   * Check if user is authenticated
   *
   * @returns {Promise<boolean>} Promise resolving to authentication status
   *
   */
  async isAuthenticated(): Promise<boolean> {
    try {
      const tokens = await WithingsTokenService.getStoredTokens();
      // Consider authenticated if tokens exist, even if refresh is required
      return !!tokens;
    } catch {
      return false;
    }
  },

  /**
   * Store OAuth2 state parameter for CSRF protection
   *
   * @param {string} state - State parameter to store
   *
   * @returns {Promise<void>} Promise resolving when state is stored
   *
   */
  async storeState(state: string): Promise<void> {
    try {
      debugHealth("debug", "withings", "Storing OAuth state parameter", {
        file: "withings-token-service.ts",
        function: "storeState",
        state: state,
      });

      await core.invoke("set_withings_oauth_state", { request: { state } });

      debugHealth("info", "withings", "OAuth state parameter stored successfully", {
        file: "withings-token-service.ts",
        function: "storeState",
      });
    } catch (error: unknown) {
      debugHealth("error", "withings", "Failed to store OAuth state parameter", {
        error: error instanceof Error ? error.message : String(error),
        file: "withings-token-service.ts",
        function: "storeState",
      });
    }
  },

  /**
   * Get stored OAuth2 state parameter
   *
   * @returns {Promise<string | undefined>} Promise resolving to state parameter or undefined
   *
   */
  async getStoredState(): Promise<string | undefined> {
    try {
      debugHealth("debug", "withings", "Retrieving stored OAuth state parameter", {
        file: "withings-token-service.ts",
        function: "getStoredState",
      });

      const state = await core.invoke<string | undefined>("get_withings_oauth_state");
      const result = state ?? undefined;

      debugHealth("debug", "withings", "Retrieved OAuth state parameter", {
        file: "withings-token-service.ts",
        function: "getStoredState",
        hasState: !!result,
        state: result,
      });

      return result;
    } catch (error: unknown) {
      debugHealth("error", "withings", "Failed to retrieve stored OAuth state parameter", {
        error: error instanceof Error ? error.message : String(error),
        file: "withings-token-service.ts",
        function: "getStoredState",
      });
      return undefined;
    }
  },

  /**
   * Clear stored OAuth2 state parameter
   *
   * @returns {Promise<void>} Promise resolving when state is cleared
   *
   */
  async clearStoredState(): Promise<void> {
    try {
      debugHealth("debug", "withings", "Clearing stored OAuth state parameter", {
        file: "withings-token-service.ts",
        function: "clearStoredState",
      });

      await core.invoke("clear_withings_oauth_state");

      debugHealth("info", "withings", "OAuth state parameter cleared successfully", {
        file: "withings-token-service.ts",
        function: "clearStoredState",
      });
    } catch (error: unknown) {
      debugHealth("error", "withings", "Failed to clear stored OAuth state parameter", {
        error: error instanceof Error ? error.message : String(error),
        file: "withings-token-service.ts",
        function: "clearStoredState",
      });
    }
  },

  /**
   * Generate cryptographically secure random state parameter for OAuth2
   * WHY: Uses crypto.getRandomValues() instead of Math.random() for security
   *
   * @returns {string} Generated state string
   *
   */
  generateState(): string {
    // Generate cryptographically secure random bytes
    const randomBytes = new Uint8Array(16);
    crypto.getRandomValues(randomBytes);

    // Convert to base36 string (similar to Math.random().toString(36))
    // This creates a URL-safe random string for OAuth2 state parameter
    const base36Chars = "0123456789abcdefghijklmnopqrstuvwxyz";
    let result = "";

    for (const randomByte of randomBytes) {
      const char = base36Chars[randomByte % base36Chars.length];
      if (char) {
        result = result + char;
      }
    }

    return result;
  },

  /**
   * Debug function to check configuration status
   *
   * @returns {Promise<void>} Promise resolving when debug is complete
   *
   */
  async debugConfig(): Promise<void> {
    try {
      // NOTE: Config retrieved but not used in this function - for debugging only
      await WithingsTokenService.getConfig();
    } catch (error: unknown) {
      // NOTE: Debug function failed - log via debug system
      debugHealth("error", "withings", "Debug config error", {
        error: error instanceof Error ? error.message : String(error),
        file: "withings-token-service.ts",
        function: "debugConfig",
      });
    }
  },
} as const;
